import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Eye, LogOut, Plus, FileText, Receipt, Search, Edit, Globe, RefreshCw, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminOrders({ user, setUser }) {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showManualOrderForm, setShowManualOrderForm] = useState(false);
  const [editingCustomerData, setEditingCustomerData] = useState(false);
  const [showCustomsModal, setShowCustomsModal] = useState(false);
  // Invoice generation modal state
  const [invoiceModal, setInvoiceModal] = useState(null); // { orderId, type, force }
  // Local draft of shipping fields — decouples typing from server saves so the
  // admin no longer triggers an email per onBlur. Synced from selectedOrder
  // every time a different order is opened.
  const [shippingDraft, setShippingDraft] = useState({ shipping_carrier: '', tracking_number: '', tracking_url: '' });
  const [savingShipping, setSavingShipping] = useState(false);
  const [notifyingCustomer, setNotifyingCustomer] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Reset shipping draft whenever the modal opens a different order so that
    // edits to one order can never leak into another.
    if (selectedOrder) {
      setShippingDraft({
        shipping_carrier: selectedOrder.shipping_carrier || '',
        tracking_number: selectedOrder.tracking_number || '',
        tracking_url: selectedOrder.tracking_url || '',
      });
    }
  }, [selectedOrder?.id]);

  useEffect(() => {
    // Filter orders based on search term
    if (searchTerm.trim() === '') {
      setFilteredOrders(orders);
    } else {
      const search = searchTerm.toLowerCase();
      const filtered = orders.filter(o => 
        o.user_name.toLowerCase().includes(search) ||
        o.user_email.toLowerCase().includes(search) ||
        (o.user_address && o.user_address.toLowerCase().includes(search)) ||
        (o.user_city && o.user_city.toLowerCase().includes(search)) ||
        (o.user_postal_code && o.user_postal_code.toLowerCase().includes(search))
      );
      setFilteredOrders(filtered);
    }
  }, [searchTerm, orders]);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setOrders(data.orders || []);
      setFilteredOrders(data.orders || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus, extraData = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus, ...extraData })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.email_sent ? 'Estado actualizado y email enviado al cliente' : 'Estado actualizado');
        fetchOrders();
        if (selectedOrder?.id === orderId) {
          setSelectedOrder({...selectedOrder, order_status: newStatus, ...extraData});
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'No se pudo actualizar el estado');
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const saveShippingInfo = async () => {
    if (!selectedOrder) return;
    setSavingShipping(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${selectedOrder.id}/shipping-info`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(shippingDraft),
      });
      if (response.ok) {
        toast.success('Datos de envío guardados (sin enviar email)');
        setSelectedOrder({ ...selectedOrder, ...shippingDraft });
        fetchOrders();
      } else {
        toast.error('No se pudieron guardar los datos de envío');
      }
    } catch (e) {
      toast.error('Error de conexión');
    } finally {
      setSavingShipping(false);
    }
  };

  const notifyCustomer = async (type) => {
    if (!selectedOrder) return;
    const labels = { processing: 'procesamiento', shipped: 'envío' };
    if (!window.confirm(`¿Enviar email de ${labels[type]} al cliente con los datos actuales?`)) return;
    setNotifyingCustomer(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${selectedOrder.id}/notify-customer`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(`Email de ${labels[type]} enviado al cliente`);
      } else {
        toast.error(data.detail || 'No se pudo enviar el email');
      }
    } catch (e) {
      toast.error('Error de conexión');
    } finally {
      setNotifyingCustomer(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const saveCustomerData = async (data) => {
    if (!selectedOrder) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${selectedOrder.id}/customer-data`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success('Datos actualizados');
        // Update local state
        setSelectedOrder({...selectedOrder, ...data});
        fetchOrders();
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const generateInvoice = async (orderId, type, payload = {}) => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/generate-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type,
          customer_nif: payload.customer_nif || null,
          billing_name: payload.billing_name || null,
          billing_address: payload.billing_address || null,
          billing_city: payload.billing_city || null,
          billing_postal_code: payload.billing_postal_code || null,
          billing_country: payload.billing_country || null,
          force_regenerate: !!payload.force_regenerate
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Download PDF
        const pdfData = atob(data.pdf);
        const bytes = new Uint8Array(pdfData.length);
        for (let i = 0; i < pdfData.length; i++) {
          bytes[i] = pdfData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${type}-${data.invoice_number}.pdf`;
        link.click();
        
        toast.success(
          payload.force_regenerate
            ? `${type === 'factura' ? 'Factura' : 'Recibo'} regenerado: ${data.invoice_number}`
            : (data.already_exists
                ? `${type === 'factura' ? 'Factura' : 'Recibo'} ya existía - Descargado`
                : `${type === 'factura' ? 'Factura' : 'Recibo'} generado: ${data.invoice_number}`)
        );
      }
    } catch (error) {
      toast.error('Error al generar documento');
    }
  };

  const openInvoiceModal = (orderId, type, force = false) => {
    if (force && !window.confirm(`¿Regenerar ${type}? Sobrescribirá el PDF existente conservando el número.`)) return;
    setInvoiceModal({ orderId, type, force });
  };

  const handleInvoiceModalSubmit = (data) => {
    if (!invoiceModal) return;
    generateInvoice(invoiceModal.orderId, invoiceModal.type, { ...data, force_regenerate: invoiceModal.force });
    setInvoiceModal(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100';
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 py-3 overflow-x-auto">
          <div className="flex gap-6 whitespace-nowrap">
            <Link to="/admin" className="text-gray-600 hover:text-black">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black">Productos</Link>
            <Link to="/admin/orders" className="font-medium text-black border-b-2 border-black pb-2">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black">Avisos</Link>
            <Link to="/admin/newsletter" className="text-gray-600 hover:text-black">Newsletter</Link>
            <Link to="/admin/gallery" className="text-gray-600 hover:text-black">Galería</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Gestión de Pedidos</h2>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por nombre, email, dirección, ciudad, CP..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
            <Button onClick={() => setShowManualOrderForm(true)} className="bg-black text-white whitespace-nowrap">
              <Plus className="w-4 h-4 mr-2" /> Crear Pedido Manual
            </Button>
            <Button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const r = await fetch(`${BACKEND_URL}/api/admin/shipping/poll-now`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  const d = await r.json();
                  if (d.success) {
                    toast.success(`Estados actualizados: ${d.polled} consultados, ${d.delivered} marcados entregados.`);
                    fetchOrders();
                  } else {
                    toast.error('No se pudo actualizar');
                  }
                } catch (e) {
                  toast.error('Error al actualizar estados');
                }
              }}
              className="border-2 border-black bg-white text-black hover:bg-gray-50 whitespace-nowrap"
              data-testid="admin-shipping-poll-btn"
              title="Consulta Correos y marca como entregados los pedidos completados"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Actualizar envíos
            </Button>
          </div>
        </div>

        {searchTerm && (
          <p className="text-sm text-gray-600 mb-4">
            {filteredOrders.length} pedido(s) encontrado(s)
          </p>
        )}

        {showManualOrderForm && (
          <ManualOrderForm 
            onClose={() => setShowManualOrderForm(false)}
            onSuccess={() => { setShowManualOrderForm(false); fetchOrders(); }}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white border-2 border-black">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b-2 border-black">
                      <th className="text-left p-4">Fecha</th>
                      <th className="text-left p-4">ID</th>
                      <th className="text-left p-4">Cliente</th>
                      <th className="text-left p-4">Total</th>
                      <th className="text-left p-4">Pago</th>
                      <th className="text-left p-4">Estado</th>
                      <th className="text-right p-4">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => (
                      <tr key={order.id} data-testid={`order-row-${order.id}`} className="border-b hover:bg-gray-50" onClick={() => setSelectedOrder(order)}>
                        <td className="p-4 text-sm font-semibold whitespace-nowrap">{new Date(order.created_at).toLocaleDateString('es-ES')}</td>
                        <td className="p-4 font-mono text-sm">{order.id.substring(0, 8)}</td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{order.user_name}</p>
                            <p className="text-sm text-gray-500">{order.user_email}</p>
                          </div>
                        </td>
                        <td className="p-4 font-bold">{order.total.toFixed(2)}€</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                            order.payment_method === 'bizum' ? 'bg-red-100 text-red-700' :
                            order.payment_method === 'transferencia' ? 'bg-orange-100 text-orange-700' :
                            order.payment_method === 'stripe' ? 'bg-purple-100 text-purple-700' :
                            order.payment_method === 'datafono_caixabank' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {order.payment_method === 'datafono_caixabank' ? 'CaixaBank' : order.payment_method}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(order.order_status)}`}>
                            {order.order_status}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className={selectedOrder ? "fixed inset-0 z-50 bg-black/60 overflow-y-auto p-3 lg:static lg:bg-transparent lg:p-0 lg:z-auto lg:overflow-visible" : ""}>
            {selectedOrder ? (
              <div className="bg-white p-6 border-2 border-black lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto" data-testid="admin-order-detail-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">Detalle del Pedido</h3>
                  <button
                    type="button"
                    onClick={() => setSelectedOrder(null)}
                    className="lg:hidden text-3xl leading-none px-3 py-1 -mr-2 hover:bg-gray-100 rounded"
                    aria-label="Cerrar"
                    data-testid="admin-order-detail-close-mobile"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">ID Pedido</p>
                    <p className="font-mono text-sm">{selectedOrder.id}</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">Cliente</p>
                      <button
                        onClick={() => setEditingCustomerData(!editingCustomerData)}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Edit className="w-3 h-3" />
                        {editingCustomerData ? 'Cancelar' : 'Editar'}
                      </button>
                    </div>
                    
                    {editingCustomerData ? (
                      <div className="space-y-2 bg-gray-50 p-3 rounded">
                        <Input
                          defaultValue={selectedOrder.user_name}
                          placeholder="Nombre"
                          onBlur={(e) => saveCustomerData({ user_name: e.target.value })}
                          className="text-sm"
                        />
                        <Input
                          defaultValue={selectedOrder.user_email}
                          placeholder="Email"
                          onBlur={(e) => saveCustomerData({ user_email: e.target.value })}
                          className="text-sm"
                        />
                        <Input
                          defaultValue={selectedOrder.user_phone}
                          placeholder="Teléfono"
                          onBlur={(e) => saveCustomerData({ user_phone: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <CopyableField label="Nombre" value={selectedOrder.user_name} bold />
                        <CopyableField label="Email" value={selectedOrder.user_email} />
                        <CopyableField label="Teléfono" value={selectedOrder.user_phone} />
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-2">Dirección de Envío</p>
                    {editingCustomerData ? (
                      <div className="space-y-2 bg-gray-50 p-3 rounded">
                        <Input
                          defaultValue={selectedOrder.user_address}
                          placeholder="Dirección"
                          onBlur={(e) => saveCustomerData({ user_address: e.target.value })}
                          className="text-sm"
                        />
                        <Input
                          defaultValue={selectedOrder.user_city}
                          placeholder="Ciudad / Población"
                          onBlur={(e) => saveCustomerData({ user_city: e.target.value })}
                          className="text-sm"
                        />
                        <Input
                          defaultValue={selectedOrder.user_province}
                          placeholder="Provincia"
                          onBlur={(e) => saveCustomerData({ user_province: e.target.value })}
                          className="text-sm"
                        />
                        <Input
                          defaultValue={selectedOrder.user_postal_code}
                          placeholder="CP"
                          onBlur={(e) => saveCustomerData({ user_postal_code: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <CopyableField label="Dirección" value={selectedOrder.user_address} />
                        <CopyableField label="Población" value={selectedOrder.user_city} />
                        <CopyableField label="Provincia" value={selectedOrder.user_province} />
                        <CopyableField label="CP" value={selectedOrder.user_postal_code} />
                        <CopyableField label="País" value={selectedOrder.user_country} />
                        <CopyableField
                          label="Etiqueta postal completa"
                          value={[
                            selectedOrder.user_name,
                            selectedOrder.user_address,
                            `${selectedOrder.user_postal_code || ''} ${selectedOrder.user_city || ''}`.trim(),
                            selectedOrder.user_province,
                            selectedOrder.user_country,
                            selectedOrder.user_phone,
                          ].filter(Boolean).join('\n')}
                          multiline
                          highlight
                        />
                      </div>
                    )}

                    {/* Shipping method: prominent banner so the operator packing
                        the order can't miss whether it's home delivery or
                        pickup at a Correos office. */}
                    {selectedOrder.shipping_method === 'office_pickup' ? (
                      <div className="mt-3 border-2 border-orange-500 bg-orange-50 p-3 rounded" data-testid="shipping-office-info">
                        <p className="font-bold text-orange-700 uppercase text-sm">📦 Recogida en oficina de Correos</p>
                        {selectedOrder.pickup_office_auto ? (
                          <p className="text-sm mt-1 text-orange-900"><strong>El cliente pide que ELIJAMOS la oficina más cercana</strong> a su domicilio ({selectedOrder.user_postal_code} {selectedOrder.user_city}).</p>
                        ) : (
                          <CopyableField
                            label="Oficina indicada por el cliente"
                            value={selectedOrder.pickup_office_address}
                            multiline
                          />
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-600" data-testid="shipping-home-info">🏠 Entrega a domicilio (Correos Premium 24-48h)</div>
                    )}
                  </div>

                  <OrderItemsEditor order={selectedOrder} onUpdate={(updatedOrder) => { setSelectedOrder(updatedOrder); fetchOrders(); }} />

                  <div className="border-t pt-3">
                    <div className="flex justify-between mb-1">
                      <span>Subtotal:</span>
                      <span>{selectedOrder.subtotal?.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span>Envío:</span>
                      <span>{selectedOrder.shipping_cost?.toFixed(2)}€</span>
                    </div>
                    {selectedOrder.cashback_used > 0 && (
                      <div className="flex justify-between mb-1 text-green-600">
                        <span>Cashback usado:</span>
                        <span>-{selectedOrder.cashback_used.toFixed(2)}€</span>
                      </div>
                    )}
                    {selectedOrder.coupon_discount > 0 && (
                      <div className="flex justify-between mb-1 text-green-600">
                        <span>Cupon ({selectedOrder.coupon_code}):</span>
                        <span>-{selectedOrder.coupon_discount.toFixed(2)}€</span>
                      </div>
                    )}
                    {(selectedOrder.manual_discount || 0) > 0 && (
                      <div className="flex justify-between mb-1 text-orange-600">
                        <span>Descuento manual{selectedOrder.manual_discount_type === 'percentage' ? ` (${selectedOrder.manual_discount}%)` : ''}:</span>
                        <span>-{(selectedOrder.manual_discount_calculated || selectedOrder.manual_discount || 0).toFixed(2)}€</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span>{selectedOrder.total?.toFixed(2)}€</span>
                    </div>
                  </div>

                  <AdminNotesField order={selectedOrder} onUpdate={(notes) => setSelectedOrder({ ...selectedOrder, admin_notes: notes })} />

                  <div>
                    <p className="text-sm text-gray-600 mb-2">Cambiar Estado</p>
                    <select 
                      className="w-full border-2 border-black p-2 rounded mb-3"
                      value={selectedOrder.order_status}
                      onChange={(e) => {
                        const newStatus = e.target.value;
                        // When transitioning to "shipped" we always include the
                        // current draft so the email goes out with the latest
                        // tracking info — even if the admin forgot to press
                        // "Guardar datos de envío" first.
                        const extraData = newStatus === 'shipped' ? { ...shippingDraft } : {};
                        handleStatusChange(selectedOrder.id, newStatus, extraData);
                      }}
                      data-testid="order-status-select"
                    >
                      <option value="pending">Pendiente</option>
                      <option value="on_hold">En Espera</option>
                      <option value="processing">Procesando</option>
                      <option value="processed">Procesado</option>
                      <option value="custom_pending">Pedido Personalizado en Espera</option>
                      <option value="shipped">Enviado</option>
                      <option value="delivered">Entregado</option>
                      <option value="cancelled">Cancelado</option>
                    </select>

                    {/* Tracking Information */}
                    <div className="space-y-3 mt-4 p-3 bg-gray-50 rounded border" data-testid="shipping-info-section">
                      <p className="text-sm font-medium">Información de Envío</p>
                      <div>
                        <Label className="text-xs">Transportista</Label>
                        <Input
                          placeholder="Ej: Correos, MRW, SEUR"
                          value={shippingDraft.shipping_carrier}
                          onChange={(e) => setShippingDraft({ ...shippingDraft, shipping_carrier: e.target.value })}
                          className="text-sm"
                          data-testid="shipping-carrier-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Número de Seguimiento</Label>
                        <Input
                          placeholder="Ej: ES123456789"
                          value={shippingDraft.tracking_number}
                          onChange={(e) => setShippingDraft({ ...shippingDraft, tracking_number: e.target.value })}
                          className="text-sm"
                          data-testid="tracking-number-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Link de Seguimiento</Label>
                        <Input
                          placeholder="https://tracking.ejemplo.com/..."
                          value={shippingDraft.tracking_url}
                          onChange={(e) => setShippingDraft({ ...shippingDraft, tracking_url: e.target.value })}
                          className="text-sm"
                          data-testid="tracking-url-input"
                        />
                      </div>
                      <Button
                        onClick={saveShippingInfo}
                        disabled={savingShipping}
                        className="w-full bg-black text-white hover:bg-gray-800"
                        data-testid="save-shipping-info-btn"
                      >
                        {savingShipping ? 'Guardando...' : '💾 Guardar datos de envío (sin email)'}
                      </Button>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          onClick={() => notifyCustomer('processing')}
                          disabled={notifyingCustomer}
                          variant="outline"
                          className="text-xs border-black"
                          data-testid="notify-processing-btn"
                        >
                          📧 Reenviar "Procesando"
                        </Button>
                        <Button
                          onClick={() => notifyCustomer('shipped')}
                          disabled={notifyingCustomer || !shippingDraft.tracking_number}
                          variant="outline"
                          className="text-xs border-black"
                          data-testid="notify-shipped-btn"
                          title={!shippingDraft.tracking_number ? 'Falta el número de seguimiento' : ''}
                        >
                          📧 Reenviar "Enviado"
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        💡 Los datos se guardan sin notificar al cliente. Solo se envía email cuando cambias el estado a <strong>Procesando</strong> o <strong>Enviado</strong>, o pulsas "Reenviar".
                      </p>
                    </div>

                    {/* Notifications log — gives the admin visibility into which
                        emails have actually been sent for this order, so they
                        never wonder "did I already notify them?". */}
                    <NotificationsLog log={selectedOrder.notifications_log} />
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">Método de Pago</p>
                    <PaymentMethodEditor
                      order={selectedOrder}
                      onUpdate={(patch) => {
                        setSelectedOrder(prev => ({ ...prev, ...patch }));
                        fetchOrders();
                      }}
                    />
                    <p className="text-sm">Estado: <span className={selectedOrder.payment_status === 'paid' ? 'text-green-600 font-bold' : selectedOrder.payment_status === 'partial' ? 'text-blue-600 font-bold' : selectedOrder.payment_status === 'pending' ? 'text-yellow-600 font-bold' : 'text-red-600 font-bold'}>{selectedOrder.payment_status === 'partial' ? 'parcial' : selectedOrder.payment_status}</span></p>
                    <PaymentStatusControls order={selectedOrder} onUpdate={(updatedOrder) => { setSelectedOrder(updatedOrder); fetchOrders(); }} />
                    <PaymentEmailStatus order={selectedOrder} onUpdate={(patch) => { setSelectedOrder({ ...selectedOrder, ...patch }); fetchOrders(); }} />
                  </div>

                  {/* Auditoría / origen del pedido — visible sólo si hay datos */}
                  {selectedOrder.audit && (selectedOrder.audit.client_ip || selectedOrder.audit.user_agent) && (
                    <details className="col-span-2 mt-2 border border-gray-200 rounded p-3 bg-gray-50" data-testid="order-audit-details">
                      <summary className="cursor-pointer text-sm font-semibold">
                        Origen del pedido ({selectedOrder.audit.client_ip || 'IP desconocida'})
                      </summary>
                      <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div><dt className="inline font-medium text-gray-600">IP: </dt><dd className="inline text-gray-800">{selectedOrder.audit.client_ip || '—'}</dd></div>
                        <div><dt className="inline font-medium text-gray-600">Referrer: </dt><dd className="inline break-all text-gray-800">{selectedOrder.audit.referrer || '—'}</dd></div>
                        <div><dt className="inline font-medium text-gray-600">Origin: </dt><dd className="inline break-all text-gray-800">{selectedOrder.audit.origin || '—'}</dd></div>
                        <div><dt className="inline font-medium text-gray-600">Idioma: </dt><dd className="inline text-gray-800">{selectedOrder.audit.accept_language || '—'}</dd></div>
                        <div className="col-span-2"><dt className="inline font-medium text-gray-600">User-Agent: </dt><dd className="inline break-all text-gray-800">{selectedOrder.audit.user_agent || '—'}</dd></div>
                      </dl>
                    </details>
                  )}

                  {/* Pagos parciales (tarjeta + efectivo, 50/50 personalizados, etc.) */}
                  <PartialPaymentsSection
                    order={selectedOrder}
                    onUpdate={(patch) => {
                      setSelectedOrder(prev => ({ ...prev, ...patch }));
                      fetchOrders();
                    }}
                  />

                  {/* Stripe Refund Section */}
                  {selectedOrder.payment_method === 'stripe' && selectedOrder.payment_status === 'paid' && (
                    <RefundSection order={selectedOrder} onRefundComplete={() => { fetchOrders(); setSelectedOrder({...selectedOrder, payment_status: 'partially_refunded'}); }} />
                  )}

                  {/* Contact Client */}
                  <ContactClientSection order={selectedOrder} />

                  {/* Invoice/Receipt Generation */}
                  <div className="border-t pt-4 space-y-3">
                    <p className="font-medium mb-3">Documentos Fiscales</p>
                    <Button 
                      onClick={() => openInvoiceModal(selectedOrder.id, 'factura')}
                      className="w-full bg-black text-white"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Generar Factura (IVA incluido)
                    </Button>
                    <Button 
                      onClick={() => openInvoiceModal(selectedOrder.id, 'recibo')}
                      className="w-full border-2 border-black bg-white text-black hover:bg-black hover:text-white"
                    >
                      <Receipt className="w-4 h-4 mr-2" />
                      Generar Recibo (simplificado)
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        onClick={() => openInvoiceModal(selectedOrder.id, 'factura', true)}
                        className="text-xs border border-orange-400 bg-white text-orange-700 hover:bg-orange-50 py-1"
                        data-testid="regenerate-factura-btn"
                      >
                        Regenerar factura
                      </Button>
                      <Button
                        type="button"
                        onClick={() => openInvoiceModal(selectedOrder.id, 'recibo', true)}
                        className="text-xs border border-orange-400 bg-white text-orange-700 hover:bg-orange-50 py-1"
                        data-testid="regenerate-recibo-btn"
                      >
                        Regenerar recibo
                      </Button>
                    </div>
                    {selectedOrder.user_country && selectedOrder.user_country !== 'España' && (
                      <Button
                        data-testid="customs-invoice-btn"
                        onClick={() => setShowCustomsModal(true)}
                        className="w-full border-2 border-blue-600 bg-white text-blue-600 hover:bg-blue-600 hover:text-white"
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        Factura de Aduana
                      </Button>
                    )}
                  </div>

                  {/* Customer Account section — always visible, clearer UX */}
                  <div className="border-t pt-4" data-testid="customer-account-section">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">👤</span>
                      <p className="font-medium">Cuenta del cliente</p>
                    </div>
                    {selectedOrder.is_guest ? (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-700">
                          Este pedido se realizó como <strong>invitado</strong> (sin cuenta registrada).
                        </p>
                        <Button
                          onClick={async () => {
                            if (!window.confirm(`Crear cuenta para ${selectedOrder.user_email} y enviar contraseña por email?`)) return;
                            try {
                              const token = localStorage.getItem('token');
                              const response = await fetch(`${BACKEND_URL}/api/admin/orders/${selectedOrder.id}/convert-to-customer`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              const data = await response.json();
                              if (data.success) {
                                toast.success('Cuenta creada. Email enviado con credenciales.');
                                fetchOrders();
                              } else {
                                toast.error(data.message || 'Error al convertir');
                              }
                            } catch (error) {
                              toast.error('Error de conexión');
                            }
                          }}
                          className="w-full bg-green-600 text-white hover:bg-green-700"
                          data-testid="convert-to-customer-btn"
                        >
                          ✉️ Crear cuenta y enviar credenciales al cliente
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-700">
                          Cliente <strong className="text-green-700">registrado</strong>
                          {selectedOrder.user_id ? '' : ' (sin user_id asociado)'}
                        </p>
                        <Button
                          variant="outline"
                          className="w-full border-black text-black hover:bg-gray-100"
                          data-testid="resend-credentials-btn"
                          onClick={async () => {
                            if (!window.confirm(`Generar una nueva contraseña para ${selectedOrder.user_email} y enviársela por email? La contraseña actual dejará de funcionar.`)) return;
                            try {
                              const token = localStorage.getItem('token');
                              const response = await fetch(`${BACKEND_URL}/api/admin/orders/${selectedOrder.id}/resend-credentials`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              const data = await response.json();
                              if (data.success) {
                                toast.success('Nueva contraseña enviada por email al cliente.');
                              } else {
                                toast.error(data.message || data.detail || 'Error');
                              }
                            } catch (error) {
                              toast.error('Error de conexión');
                            }
                          }}
                        >
                          🔑 Reenviar credenciales (nueva contraseña)
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Cancel Order */}
                  <div className="border-t pt-4 space-y-3">
                    <Button 
                      onClick={() => {
                        if (confirm('¿Cancelar este pedido?')) {
                          handleStatusChange(selectedOrder.id, 'cancelled');
                        }
                      }}
                      className="w-full border-2 border-red-600 bg-white text-red-600 hover:bg-red-600 hover:text-white"
                    >
                      Cancelar Pedido
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white p-6 border-2 border-black text-center text-gray-500">
                <ShoppingCart className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p>Selecciona un pedido para ver los detalles</p>
              </div>
            )}
          </div>
        </div>
        {showCustomsModal && selectedOrder && (
          <CustomsInvoiceModal
            order={selectedOrder}
            onClose={() => setShowCustomsModal(false)}
          />
        )}
        {invoiceModal && selectedOrder && (
          <InvoiceDataModal
            order={selectedOrder}
            type={invoiceModal.type}
            force={invoiceModal.force}
            onCancel={() => setInvoiceModal(null)}
            onSubmit={handleInvoiceModalSubmit}
          />
        )}
      </div>
    </div>
  );
}


// Click-to-copy field for the admin order detail. Shows the value with a copy
// icon; click puts the value (or a multiline label like the postal address) on
// the clipboard.
function CopyableField({ label, value, multiline = false, highlight = false, bold = false }) {
  if (!value) return null;
  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiado`);
    } catch (err) {
      toast.error('No se pudo copiar');
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`group w-full text-left flex items-start gap-2 px-2 py-1 rounded transition-colors ${highlight ? 'bg-amber-50 border border-amber-200 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
      data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`}
      title={`Click para copiar ${label}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
        <p className={`text-sm break-words ${bold ? 'font-bold' : ''} ${multiline ? 'whitespace-pre-line' : ''}`}>{value}</p>
      </div>
      <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-black flex-shrink-0 mt-0.5" />
    </button>
  );
}


// Internal admin observations textarea — saved with explicit button to avoid
// spamming the API on every keystroke.
function AdminNotesField({ order, onUpdate }) {
  const [value, setValue] = useState(order.admin_notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(order.admin_notes || '');
  }, [order.id, order.admin_notes]);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/admin-notes`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_notes: value }),
      });
      if (r.ok) {
        toast.success('Observaciones guardadas');
        onUpdate(value);
      } else {
        toast.error('No se pudo guardar');
      }
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const dirty = (order.admin_notes || '') !== value;

  return (
    <div className="border-2 border-dashed border-gray-300 p-3 rounded bg-gray-50" data-testid="admin-notes-block">
      <label className="block text-sm font-bold mb-1">Observaciones internas</label>
      <p className="text-xs text-gray-500 mb-2">Solo visible para administración. No se envía al cliente.</p>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        className="w-full border border-gray-300 p-2 rounded text-sm resize-y"
        placeholder="Ej. cliente solicita entrega tras 18h, regalo, recoger en oficina..."
        data-testid="admin-notes-textarea"
      />
      <div className="flex justify-end mt-2">
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="text-xs bg-black text-white disabled:bg-gray-300 disabled:cursor-not-allowed py-1 px-3"
          data-testid="admin-notes-save-btn"
        >
          {saving ? 'Guardando...' : 'Guardar observaciones'}
        </Button>
      </div>
    </div>
  );
}


// Invoice / Receipt data modal — NIF + optional billing address override
function InvoiceDataModal({ order, type, force, onCancel, onSubmit }) {
  const [customerNif, setCustomerNif] = useState(order.customer_nif || '');
  const [differentBilling, setDifferentBilling] = useState(false);
  const [billing, setBilling] = useState({
    name: order.user_name || '',
    address: order.user_address || '',
    city: order.user_city || '',
    postal_code: order.user_postal_code || '',
    country: order.user_country || 'España',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      customer_nif: customerNif.trim() || null,
      billing_name: differentBilling ? billing.name.trim() || null : null,
      billing_address: differentBilling ? billing.address.trim() || null : null,
      billing_city: differentBilling ? billing.city.trim() || null : null,
      billing_postal_code: differentBilling ? billing.postal_code.trim() || null : null,
      billing_country: differentBilling ? billing.country.trim() || null : null,
    });
  };

  const docLabel = type === 'factura' ? 'Factura' : 'Recibo';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="invoice-data-modal">
      <form onSubmit={handleSubmit} className="bg-white border-2 border-black w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">
            {force ? `Regenerar ${docLabel}` : `Generar ${docLabel}`}
          </h3>
          <button type="button" onClick={onCancel} className="text-2xl leading-none px-2 hover:bg-gray-100 rounded" aria-label="Cerrar" data-testid="invoice-modal-close">×</button>
        </div>

        <div className="space-y-4">
          <div>
            <Label>NIF / CIF del cliente (opcional)</Label>
            <Input
              type="text"
              value={customerNif}
              onChange={(e) => setCustomerNif(e.target.value)}
              placeholder="Ej. 12345678A"
              data-testid="invoice-nif-input"
            />
          </div>

          <div className="border-t pt-4">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Dirección de envío del pedido:</strong>
              <br />
              {order.user_name}
              <br />
              {order.user_address}
              <br />
              {order.user_postal_code} {order.user_city} ({order.user_country})
            </p>

            <label className="flex items-start gap-2 cursor-pointer" data-testid="different-billing-label">
              <input
                type="checkbox"
                checked={differentBilling}
                onChange={(e) => setDifferentBilling(e.target.checked)}
                className="mt-1 w-4 h-4"
                data-testid="different-billing-checkbox"
              />
              <span className="text-sm">
                <span className="font-bold">La dirección de facturación es distinta</span>
                <span className="block text-xs text-gray-600">Marca esta casilla si el cliente quiere la factura a nombre o dirección distintos. Si no, dejaremos los datos del envío.</span>
              </span>
            </label>
          </div>

          {differentBilling && (
            <div className="border-2 border-dashed border-orange-300 bg-orange-50 p-4 rounded space-y-3" data-testid="billing-fields">
              <div>
                <Label>Nombre / Razón social *</Label>
                <Input
                  required
                  type="text"
                  value={billing.name}
                  onChange={(e) => setBilling({ ...billing, name: e.target.value })}
                  data-testid="billing-name-input"
                />
              </div>
              <div>
                <Label>Dirección *</Label>
                <Input
                  required
                  type="text"
                  value={billing.address}
                  onChange={(e) => setBilling({ ...billing, address: e.target.value })}
                  data-testid="billing-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ciudad *</Label>
                  <Input
                    required
                    type="text"
                    value={billing.city}
                    onChange={(e) => setBilling({ ...billing, city: e.target.value })}
                    data-testid="billing-city-input"
                  />
                </div>
                <div>
                  <Label>Código Postal *</Label>
                  <Input
                    required
                    type="text"
                    value={billing.postal_code}
                    onChange={(e) => setBilling({ ...billing, postal_code: e.target.value })}
                    data-testid="billing-cp-input"
                  />
                </div>
              </div>
              <div>
                <Label>País</Label>
                <Input
                  type="text"
                  value={billing.country}
                  onChange={(e) => setBilling({ ...billing, country: e.target.value })}
                  data-testid="billing-country-input"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <Button type="submit" className="bg-black text-white" data-testid="invoice-modal-submit">
            {force ? 'Regenerar y descargar' : 'Generar y descargar'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
        </div>
      </form>
    </div>
  );
}


// Manual Order Form Component
function ManualOrderForm({ onClose, onSuccess }) {
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    customer_address: '', customer_city: '', customer_province: '', customer_postal_code: '',
    customer_country: 'España', customer_nif: '',
    payment_method: 'efectivo', notes: '',
    manual_discount_type: 'none', // 'none' | 'amount' | 'percent'
    manual_discount_value: '',
    manual_discount_reason: ''
  });
  // Order date (defaults to today in YYYY-MM-DD). Used for offline/past sales.
  const todayStr = new Date().toISOString().slice(0, 10);
  const [orderDate, setOrderDate] = useState(todayStr);
  // Mark order as already paid (for offline sales already collected)
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  // Existing customer lookup
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomerTier, setSelectedCustomerTier] = useState(null); // {tier, rate}

  useEffect(() => { fetchProducts(); fetchCustomers(); }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`);
      setProducts(await response.json());
    } catch (error) { console.error('Error:', error); }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (error) { console.error('Error:', error); }
  };

  const filteredCustomers = (() => {
    const term = customerSearch.trim().toLowerCase();
    if (!term) return [];
    return customers.filter(c => {
      const fields = [c.name, c.last_name, c.email, c.phone, c.nif, c.address, c.city, c.province, c.postal_code];
      return fields.some(f => f && String(f).toLowerCase().includes(term));
    }).slice(0, 8);
  })();

  const selectExistingCustomer = (c) => {
    const fullName = [c.name, c.last_name].filter(Boolean).join(' ').trim() || c.name || '';
    setFormData(prev => ({
      ...prev,
      customer_name: fullName,
      customer_email: c.email || '',
      customer_phone: c.phone || '',
      customer_address: c.address || '',
      customer_city: c.city || '',
      customer_province: c.province || prev.customer_province || '',
      customer_postal_code: c.postal_code || '',
      customer_country: c.country || prev.customer_country || 'España',
      customer_nif: c.nif || prev.customer_nif || '',
    }));
    setSelectedCustomerId(c.id);
    // Mirror backend TIER_DISCOUNTS so the admin sees the discount preview
    const TIER_RATES = { pro: 0.30, platino: 0.20, oro: 0.10, plata: 0.05, bronce: 0 };
    const tier = (c.loyalty_tier || 'bronce').toLowerCase();
    const rate = TIER_RATES[tier] || 0;
    setSelectedCustomerTier({ tier, rate });
    setCustomerSearch('');
    toast.success(`Datos de ${fullName || c.email} cargados${rate > 0 ? ` · descuento ${Math.round(rate*100)}% (${tier.toUpperCase()})` : ''}`);
  };

  const clearExistingCustomer = () => {
    setSelectedCustomerId(null);
    setSelectedCustomerTier(null);
    setFormData(prev => ({
      ...prev,
      customer_name: '', customer_email: '', customer_phone: '',
      customer_address: '', customer_city: '', customer_province: '',
      customer_postal_code: '', customer_nif: '',
    }));
  };

  const addItem = () => {
    const id = Date.now();
    setSelectedItems([...selectedItems, { _key: id, product_id: '', quantity: 1, price: 0, product_name: '' }]);
    setSearchTerms(prev => ({ ...prev, [id]: '' }));
  };

  const addCustomItem = () => {
    // Line filled in manually by the admin (no product catalog entry needed)
    const id = Date.now();
    setSelectedItems([...selectedItems, {
      _key: id,
      product_id: `custom:${id}`,
      product_name: '',
      quantity: 1,
      price: 0,
      is_custom: true,
    }]);
  };

  const selectProduct = (itemKey, product) => {
    setSelectedItems(prev => prev.map(item =>
      item._key === itemKey
        ? { ...item, product_id: product.id, product_name: product.name, price: product.price }
        : item
    ));
    setSearchTerms(prev => ({ ...prev, [itemKey]: '' }));
  };

  const updateItem = (itemKey, field, value) => {
    setSelectedItems(prev => prev.map(item =>
      item._key === itemKey ? { ...item, [field]: value } : item
    ));
  };

  const removeItem = (itemKey) => {
    setSelectedItems(prev => prev.filter(item => item._key !== itemKey));
    setSearchTerms(prev => { const n = { ...prev }; delete n[itemKey]; return n; });
    setCouponResult(null);
  };

  const getFilteredProducts = (itemKey) => {
    const term = (searchTerms[itemKey] || '').toLowerCase();
    if (!term) return [];
    return products.filter(p =>
      p.name.toLowerCase().includes(term) ||
      (p.brand || '').toLowerCase().includes(term) ||
      (p.sku || '').toLowerCase().includes(term)
    ).slice(0, 8);
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          code: couponCode,
          subtotal: subtotal,
          items: selectedItems.filter(i => i.product_id).map(i => ({ product_id: i.product_id, price: i.price, quantity: i.quantity }))
        })
      });
      const data = await response.json();
      if (data.valid) {
        setCouponResult(data);
        toast.success(`Cupón aplicado: -${data.discount_amount.toFixed(2)}€`);
      } else {
        setCouponResult(null);
        toast.error(data.error || 'Cupón no válido');
      }
    } catch (error) { toast.error('Error validando cupón'); }
  };

  const removeCoupon = () => { setCouponCode(''); setCouponResult(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = selectedItems.filter(i => i.product_id);
    if (validItems.length === 0) {
      toast.error('Añade al menos un producto'); return;
    }
    const incompleteCustom = validItems.find(i => i.is_custom && (!i.product_name || !i.product_name.trim()));
    if (incompleteCustom) {
      toast.error('Las líneas libres deben tener una descripción'); return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/manual`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          customer_user_id: selectedCustomerId || null,
          items: validItems.map(({ _key, ...rest }) => rest),
          coupon_code: couponResult?.code || null,
          // Manual discount (either amount or percent). Backend picks percent
          // when both are present, so only one is sent.
          manual_discount_amount: formData.manual_discount_type === 'amount' ? parseFloat(formData.manual_discount_value) || null : null,
          manual_discount_percent: formData.manual_discount_type === 'percent' ? parseFloat(formData.manual_discount_value) || null : null,
          manual_discount_reason: formData.manual_discount_type !== 'none' ? formData.manual_discount_reason || null : null,
          // Only send created_at if admin chose a past date (not today)
          created_at: orderDate && orderDate !== todayStr
            ? new Date(orderDate + 'T12:00:00').toISOString()
            : null
        })
      });
      if (response.ok) { toast.success('Pedido manual creado'); onSuccess(); }
      else { const d = await response.json().catch(() => ({})); toast.error(d.detail || 'Error al crear pedido'); }
    } catch (error) { toast.error('Error de conexión'); }
  };

  const subtotal = selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discount = couponResult?.discount_amount || 0;
  // VIP tier discount (from selected registered customer)
  const tierDiscountAmount = selectedCustomerTier?.rate > 0 ? Math.round(subtotal * selectedCustomerTier.rate * 100) / 100 : 0;
  const subtotalAfterTier = Math.max(0, subtotal - tierDiscountAmount);
  // Manual discount preview (server is the source of truth on submit).
  const manualDiscountValue = parseFloat(formData.manual_discount_value) || 0;
  const manualDiscountAmount = formData.manual_discount_type === 'percent'
    ? Math.min(100, manualDiscountValue) * subtotalAfterTier / 100
    : formData.manual_discount_type === 'amount'
      ? Math.min(manualDiscountValue, subtotalAfterTier)
      : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6 overflow-y-auto">
      <div className="bg-white p-8 border-2 border-black max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold">Crear Pedido Manual</h3>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Existing customer lookup */}
          <div className="border-2 border-dashed border-gray-400 p-4 rounded bg-gray-50">
            <h4 className="font-bold mb-2 text-sm">¿Cliente ya registrado? (opcional)</h4>
            <p className="text-xs text-gray-600 mb-3">Búscalo por nombre, email o teléfono y rellenaremos los datos automáticamente.</p>
            {selectedCustomerId ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-300 p-3 rounded" data-testid="manual-order-customer-loaded">
                <div className="text-sm">
                  <span className="font-bold text-green-800">✓ Cliente cargado:</span>
                  <span className="ml-2 text-green-900">{formData.customer_name} — {formData.customer_email}</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-300"
                  onClick={clearExistingCustomer}
                  data-testid="manual-order-clear-customer-btn"
                >
                  Quitar
                </Button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  className="w-full border-2 border-gray-300 p-2 rounded text-sm focus:border-black"
                  placeholder="Buscar cliente por nombre, email o teléfono..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  data-testid="manual-order-customer-search"
                />
                {customerSearch.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border-2 border-black rounded-b shadow-lg z-20 max-h-60 overflow-y-auto">
                    {filteredCustomers.length > 0 ? (
                      filteredCustomers.map(c => {
                        const fullName = [c.name, c.last_name].filter(Boolean).join(' ').trim() || c.name || '(sin nombre)';
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => selectExistingCustomer(c)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b text-sm"
                            data-testid={`manual-order-customer-option-${c.id}`}
                          >
                            <div className="font-medium">{fullName}</div>
                            <div className="text-xs text-gray-600">
                              {c.email}{c.phone ? ` · ${c.phone}` : ''}{c.city ? ` · ${c.city}` : ''}
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">Sin resultados ({customers.length} clientes en total)</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border p-4 rounded">
            <h4 className="font-bold mb-4">Datos del Cliente</h4>
            <div className="grid grid-cols-2 gap-4">
              <input className="border p-2 rounded" placeholder="Nombre *" value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} required />
              <input type="email" className="border p-2 rounded" placeholder="Email *" value={formData.customer_email} onChange={(e) => setFormData({...formData, customer_email: e.target.value})} required />
              <input className="border p-2 rounded" placeholder="Teléfono *" value={formData.customer_phone} onChange={(e) => setFormData({...formData, customer_phone: e.target.value})} required />
              <input className="border p-2 rounded" placeholder="NIF (opcional)" value={formData.customer_nif} onChange={(e) => setFormData({...formData, customer_nif: e.target.value})} />
              <input className="border p-2 rounded col-span-2" placeholder="Dirección *" value={formData.customer_address} onChange={(e) => setFormData({...formData, customer_address: e.target.value})} required />
              <input className="border p-2 rounded" placeholder="Ciudad *" value={formData.customer_city} onChange={(e) => setFormData({...formData, customer_city: e.target.value})} required />
              <input className="border p-2 rounded" placeholder="Provincia" value={formData.customer_province} onChange={(e) => setFormData({...formData, customer_province: e.target.value})} />
              <input className="border p-2 rounded" placeholder="Código Postal" value={formData.customer_postal_code} onChange={(e) => setFormData({...formData, customer_postal_code: e.target.value})} />
            </div>
          </div>

          {/* Order date (defaults to today; allow past dates for offline sales) */}
          <div className="border p-4 rounded">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="text-sm font-bold block mb-1">Fecha del pedido</label>
                <p className="text-xs text-gray-600 mb-2">Por defecto la fecha actual. Cámbiala para registrar una venta de un día anterior.</p>
                <input
                  type="date"
                  className="border p-2 rounded w-full sm:w-auto"
                  value={orderDate}
                  max={todayStr}
                  onChange={(e) => setOrderDate(e.target.value || todayStr)}
                  data-testid="manual-order-date-input"
                />
              </div>
              {orderDate !== todayStr && (
                <button
                  type="button"
                  onClick={() => setOrderDate(todayStr)}
                  className="text-xs text-blue-600 hover:underline self-start sm:self-end pb-2"
                  data-testid="manual-order-date-reset"
                >
                  Volver a hoy
                </button>
              )}
            </div>

            {/* Already paid toggle (useful for offline collected sales) */}
            <label className="flex items-start gap-3 mt-4 cursor-pointer" data-testid="manual-order-already-paid-label">
              <input
                type="checkbox"
                checked={alreadyPaid}
                onChange={(e) => setAlreadyPaid(e.target.checked)}
                className="mt-1 w-4 h-4"
                data-testid="manual-order-already-paid-checkbox"
              />
              <span className="text-sm">
                <span className="font-bold">Ya cobrado</span>
                <span className="block text-xs text-gray-600">
                  Marca esta opción si ya has recibido el pago (efectivo, transferencia confirmada, etc.). El pedido se creará como <strong>pagado</strong> en lugar de <em>pendiente</em>.
                </span>
              </span>
            </label>
          </div>

          <div className="border p-4 rounded">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold">Productos</h4>
              <div className="flex gap-2">
                <Button type="button" onClick={addItem} size="sm" className="bg-black text-white" data-testid="add-product-btn">+ Añadir producto</Button>
                <Button
                  type="button"
                  onClick={addCustomItem}
                  size="sm"
                  variant="outline"
                  className="border-2 border-black"
                  data-testid="add-custom-product-btn"
                  title="Añadir una línea con nombre y precio (producto que no está en el catálogo)"
                >
                  + Línea libre
                </Button>
              </div>
            </div>
            
            {selectedItems.map((item) => (
              <div key={item._key} className="mb-3 p-3 bg-gray-50 rounded border">
                {item.is_custom ? (
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-6">
                      <label className="text-xs text-gray-500">Descripción</label>
                      <input
                        type="text"
                        placeholder="Ej. Servicio postventa, ajuste a medida..."
                        className="w-full border p-1.5 rounded text-sm"
                        value={item.product_name}
                        onChange={(e) => updateItem(item._key, 'product_name', e.target.value)}
                        data-testid={`custom-product-name-${item._key}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500">Uds.</label>
                      <input type="number" min="1" className="w-full border p-1.5 rounded text-sm" value={item.quantity} onChange={(e) => updateItem(item._key, 'quantity', parseInt(e.target.value) || 1)} />
                    </div>
                    <div className="col-span-3">
                      <label className="text-xs text-gray-500">Precio (IVA incl.)</label>
                      <input type="number" step="0.01" className="w-full border p-1.5 rounded text-sm" value={item.price} onChange={(e) => updateItem(item._key, 'price', parseFloat(e.target.value) || 0)} data-testid={`custom-product-price-${item._key}`} />
                    </div>
                    <Button type="button" variant="destructive" size="sm" onClick={() => removeItem(item._key)} className="col-span-1">X</Button>
                  </div>
                ) : !item.product_id ? (
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full border-2 border-gray-300 p-2 rounded text-sm focus:border-black"
                      placeholder="Buscar producto por nombre, marca o SKU..."
                      value={searchTerms[item._key] || ''}
                      onChange={(e) => setSearchTerms(prev => ({ ...prev, [item._key]: e.target.value }))}
                      autoFocus
                      data-testid={`product-search-${item._key}`}
                    />
                    {(searchTerms[item._key] || '').length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border-2 border-black rounded-b shadow-lg z-10 max-h-48 overflow-y-auto">
                        {getFilteredProducts(item._key).length > 0 ? (
                          getFilteredProducts(item._key).map(p => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => selectProduct(item._key, p)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b text-sm flex justify-between"
                            >
                              <span className="font-medium">{p.name}</span>
                              <span className="text-gray-600 ml-2">{p.price.toFixed(2)}€</span>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-6">
                      <p className="font-medium text-sm">{item.product_name}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500">Uds.</label>
                      <input type="number" min="1" className="w-full border p-1.5 rounded text-sm" value={item.quantity} onChange={(e) => updateItem(item._key, 'quantity', parseInt(e.target.value) || 1)} />
                    </div>
                    <div className="col-span-3">
                      <label className="text-xs text-gray-500">Precio</label>
                      <input type="number" step="0.01" className="w-full border p-1.5 rounded text-sm" value={item.price} onChange={(e) => updateItem(item._key, 'price', parseFloat(e.target.value) || 0)} />
                    </div>
                    <Button type="button" variant="destructive" size="sm" onClick={() => removeItem(item._key)} className="col-span-1">X</Button>
                  </div>
                )}
              </div>
            ))}

            {selectedItems.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">Haz clic en "+ Añadir producto" para empezar</p>
            )}
          </div>

          {/* Coupon section */}
          <div className="border p-4 rounded">
            <h4 className="font-bold mb-3">Cupón de descuento</h4>
            {couponResult ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-300 p-3 rounded">
                <div>
                  <span className="font-bold text-green-800">{couponResult.code}</span>
                  <span className="text-green-700 ml-2">-{couponResult.discount_amount.toFixed(2)}€</span>
                  {couponResult.description && <p className="text-xs text-green-600 mt-1">{couponResult.description}</p>}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={removeCoupon} className="text-red-600">Quitar</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  className="flex-1 border-2 border-gray-300 p-2 rounded text-sm"
                  placeholder="Código de cupón..."
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  data-testid="manual-coupon-input"
                />
                <Button type="button" onClick={applyCoupon} className="bg-black text-white" disabled={!couponCode.trim()} data-testid="apply-coupon-btn">Aplicar</Button>
              </div>
            )}
          </div>

          {/* Manual discount */}
          <div className="border-2 border-purple-300 bg-purple-50 p-4 rounded space-y-3" data-testid="manual-discount-section">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-semibold text-purple-800 text-sm">Descuento manual (opcional):</span>
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" name="manual_discount_type" value="none"
                  checked={formData.manual_discount_type === 'none'}
                  onChange={(e) => setFormData({...formData, manual_discount_type: e.target.value, manual_discount_value: '', manual_discount_reason: ''})}
                /> Sin descuento
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" name="manual_discount_type" value="amount"
                  checked={formData.manual_discount_type === 'amount'}
                  onChange={(e) => setFormData({...formData, manual_discount_type: e.target.value})}
                /> Importe (€)
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" name="manual_discount_type" value="percent"
                  checked={formData.manual_discount_type === 'percent'}
                  onChange={(e) => setFormData({...formData, manual_discount_type: e.target.value})}
                /> Porcentaje (%)
              </label>
            </div>
            {formData.manual_discount_type !== 'none' && (
              <div className="grid grid-cols-2 gap-3">
                <input type="number" step="0.01" min="0"
                  max={formData.manual_discount_type === 'percent' ? '100' : undefined}
                  className="border-2 border-purple-300 p-2 rounded"
                  placeholder={formData.manual_discount_type === 'percent' ? 'Ej: 10 (%)' : 'Ej: 15.00 (€)'}
                  value={formData.manual_discount_value}
                  onChange={(e) => setFormData({...formData, manual_discount_value: e.target.value})}
                  data-testid="manual-discount-value-input"
                />
                <input type="text"
                  className="border-2 border-purple-300 p-2 rounded"
                  placeholder="Motivo (opcional): fidelidad, promo..."
                  value={formData.manual_discount_reason}
                  onChange={(e) => setFormData({...formData, manual_discount_reason: e.target.value})}
                  data-testid="manual-discount-reason-input"
                />
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="border p-4 rounded bg-gray-50">
            <div className="flex justify-between text-sm mb-1">
              <span>Subtotal:</span>
              <span>{subtotal.toFixed(2)}€</span>
            </div>
            {tierDiscountAmount > 0 && (
              <div className="flex justify-between text-sm text-amber-700 mb-1" data-testid="tier-discount-preview">
                <span>Descuento VIP ({selectedCustomerTier.tier.toUpperCase()} · {Math.round(selectedCustomerTier.rate*100)}%):</span>
                <span>-{tierDiscountAmount.toFixed(2)}€</span>
              </div>
            )}
            {discount > 0 && (
              <div className="flex justify-between text-sm text-green-700 mb-1">
                <span>Descuento ({couponResult?.code}):</span>
                <span>-{discount.toFixed(2)}€</span>
              </div>
            )}
            {manualDiscountAmount > 0 && (
              <div className="flex justify-between text-sm text-purple-700 mb-1" data-testid="manual-discount-preview">
                <span>Descuento manual {formData.manual_discount_type === 'percent' ? `(${formData.manual_discount_value}%)` : ''}{formData.manual_discount_reason ? ` — ${formData.manual_discount_reason}` : ''}:</span>
                <span>-{manualDiscountAmount.toFixed(2)}€</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>Envío:</span>
              <span>Se calculará según destino</span>
            </div>
            <div className="flex justify-between font-bold text-xl border-t pt-2">
              <span>Total:</span>
              <span>{Math.max(0, subtotal - tierDiscountAmount - discount - manualDiscountAmount).toFixed(2)}€ (+ envío)</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <select className="border p-2 rounded" value={formData.payment_method} onChange={(e) => setFormData({...formData, payment_method: e.target.value})}>
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia</option>
              <option value="bizum">Bizum</option>
              <option value="stripe">Tarjeta</option>
              <option value="datafono_caixabank">Datáfono virtual CaixaBank</option>
            </select>
            <select className="border p-2 rounded" value={formData.customer_country} onChange={(e) => setFormData({...formData, customer_country: e.target.value})}>
              <option value="España">España</option>
              <option value="Francia">Francia</option>
              <option value="Portugal">Portugal</option>
            </select>
          </div>

          <textarea className="w-full border p-2 rounded" rows="3" placeholder="Notas..." value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />

          <div className="flex gap-3">
            <Button type="submit" className="bg-black text-white">Crear Pedido</Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// Customs Invoice Modal Component
function CustomsInvoiceModal({ order, onClose }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchCustomsData();
  }, []);

  const fetchCustomsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/customs-invoice`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      setData(result);
    } catch (error) {
      toast.error('Error al cargar datos de aduana');
    } finally {
      setLoading(false);
    }
  };

  const saveData = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/customs-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        toast.success('Datos guardados');
      }
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      // Save first
      await saveData();

      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/customs-invoice/pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      const result = await response.json();

      if (result.success) {
        const pdfData = atob(result.pdf);
        const bytes = new Uint8Array(pdfData.length);
        for (let i = 0; i < pdfData.length; i++) {
          bytes[i] = pdfData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `aduana-${result.invoice_number}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Factura de aduana generada: ${result.invoice_number}`);
      }
    } catch (error) {
      toast.error('Error al generar PDF');
    } finally {
      setGenerating(false);
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...data.items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'unit_value' || field === 'quantity') {
      newItems[index].total_value = parseFloat(newItems[index].unit_value || 0) * parseInt(newItems[index].quantity || 1);
    }
    setData({ ...data, items: newItems });
  };

  const addItem = () => {
    setData({
      ...data,
      items: [...data.items, {
        description: '',
        hs_code: '6704.20',
        quantity: 1,
        unit_value: 0,
        total_value: 0,
        weight_kg: 0.3,
        country_of_origin: 'España'
      }]
    });
  };

  const removeItem = (index) => {
    const newItems = data.items.filter((_, i) => i !== index);
    setData({ ...data, items: newItems });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-8 border-2 border-black rounded">Cargando...</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="customs-invoice-modal">
      <div className="bg-white border-2 border-black w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b-2 border-black px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
            Factura Proforma / Aduana - Pedido #{order.id.substring(0, 8)}
          </h3>
          <div className="flex items-center gap-2">
            <Button data-testid="save-customs-btn" onClick={saveData} variant="outline" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
            <Button data-testid="generate-customs-pdf-btn" onClick={generatePDF} className="bg-blue-600 text-white" disabled={generating}>
              {generating ? 'Generando...' : 'Generar PDF'}
            </Button>
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Sender / Receiver */}
          <div className="grid grid-cols-2 gap-6">
            <div className="border p-4 rounded">
              <h4 className="font-bold mb-3">Remitente / Sender</h4>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-500">Nombre / Name</label>
                  <input className="w-full border p-2 rounded text-sm" value={data.sender.name || ''} onChange={(e) => setData({ ...data, sender: { ...data.sender, name: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs text-gray-500">NIF</label>
                  <input className="w-full border p-2 rounded text-sm" value={data.sender.nif || ''} onChange={(e) => setData({ ...data, sender: { ...data.sender, nif: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Direccion / Address</label>
                  <input className="w-full border p-2 rounded text-sm" value={data.sender.address || ''} onChange={(e) => setData({ ...data, sender: { ...data.sender, address: e.target.value } })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">CP</label>
                    <input className="w-full border p-2 rounded text-sm" value={data.sender.postal_code || ''} onChange={(e) => setData({ ...data, sender: { ...data.sender, postal_code: e.target.value } })} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Ciudad / City</label>
                    <input className="w-full border p-2 rounded text-sm" value={data.sender.city || ''} onChange={(e) => setData({ ...data, sender: { ...data.sender, city: e.target.value } })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Telefono</label>
                    <input className="w-full border p-2 rounded text-sm" value={data.sender.phone || ''} onChange={(e) => setData({ ...data, sender: { ...data.sender, phone: e.target.value } })} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Email</label>
                    <input className="w-full border p-2 rounded text-sm" value={data.sender.email || ''} onChange={(e) => setData({ ...data, sender: { ...data.sender, email: e.target.value } })} />
                  </div>
                </div>
              </div>
            </div>

            <div className="border p-4 rounded">
              <h4 className="font-bold mb-3">Destinatario / Receiver</h4>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-500">Nombre / Name</label>
                  <input className="w-full border p-2 rounded text-sm" value={data.receiver.name || ''} onChange={(e) => setData({ ...data, receiver: { ...data.receiver, name: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs text-gray-500">NIF/VAT</label>
                  <input className="w-full border p-2 rounded text-sm" value={data.receiver.nif || ''} onChange={(e) => setData({ ...data, receiver: { ...data.receiver, nif: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Direccion / Address</label>
                  <input className="w-full border p-2 rounded text-sm" value={data.receiver.address || ''} onChange={(e) => setData({ ...data, receiver: { ...data.receiver, address: e.target.value } })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">CP</label>
                    <input className="w-full border p-2 rounded text-sm" value={data.receiver.postal_code || ''} onChange={(e) => setData({ ...data, receiver: { ...data.receiver, postal_code: e.target.value } })} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Ciudad / City</label>
                    <input className="w-full border p-2 rounded text-sm" value={data.receiver.city || ''} onChange={(e) => setData({ ...data, receiver: { ...data.receiver, city: e.target.value } })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Pais / Country</label>
                    <input className="w-full border p-2 rounded text-sm" value={data.receiver.country || ''} onChange={(e) => setData({ ...data, receiver: { ...data.receiver, country: e.target.value } })} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Telefono</label>
                    <input className="w-full border p-2 rounded text-sm" value={data.receiver.phone || ''} onChange={(e) => setData({ ...data, receiver: { ...data.receiver, phone: e.target.value } })} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Shipment Info */}
          <div className="border p-4 rounded">
            <h4 className="font-bold mb-3">Informacion del Envio / Shipment Info</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs text-gray-500">Motivo / Purpose</label>
                <select className="w-full border p-2 rounded text-sm" value={data.shipment_purpose || 'Venta comercial'} onChange={(e) => setData({ ...data, shipment_purpose: e.target.value })}>
                  <option value="Venta comercial">Venta comercial</option>
                  <option value="Muestra comercial">Muestra comercial</option>
                  <option value="Regalo">Regalo</option>
                  <option value="Devolucion">Devolucion</option>
                  <option value="Reparacion">Reparacion</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Pais origen / Origin</label>
                <input className="w-full border p-2 rounded text-sm" value={data.country_of_origin || 'España'} onChange={(e) => setData({ ...data, country_of_origin: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Peso total (kg)</label>
                <input type="number" step="0.01" className="w-full border p-2 rounded text-sm" value={data.total_weight_kg || 0} onChange={(e) => setData({ ...data, total_weight_kg: parseFloat(e.target.value) })} />
              </div>
              <div>
                <label className="text-xs text-gray-500">N. Paquetes / Packages</label>
                <input type="number" min="1" className="w-full border p-2 rounded text-sm" value={data.total_packages || 1} onChange={(e) => setData({ ...data, total_packages: parseInt(e.target.value) })} />
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="border p-4 rounded">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold">Articulos / Items</h4>
              <Button type="button" size="sm" onClick={addItem} className="bg-black text-white">+ Añadir</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-1">Descripcion</th>
                    <th className="text-left py-2 px-1">Cod.Arancelario (HS)</th>
                    <th className="text-left py-2 px-1">Cant.</th>
                    <th className="text-left py-2 px-1">Valor Unit.(EUR)</th>
                    <th className="text-left py-2 px-1">Peso(kg)</th>
                    <th className="text-left py-2 px-1">Origen</th>
                    <th className="text-left py-2 px-1">Total</th>
                    <th className="py-2 px-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, idx) => (
                    <tr key={`customs-item-${item.product_id || item.description || idx}-${idx}`} className="border-b">
                      <td className="py-2 px-1">
                        <input className="w-full border p-1 rounded text-xs" value={item.description || ''} onChange={(e) => updateItem(idx, 'description', e.target.value)} />
                      </td>
                      <td className="py-2 px-1">
                        <input className="w-24 border p-1 rounded text-xs" value={item.hs_code || '6704.20'} onChange={(e) => updateItem(idx, 'hs_code', e.target.value)} />
                      </td>
                      <td className="py-2 px-1">
                        <input type="number" min="1" className="w-16 border p-1 rounded text-xs" value={item.quantity || 1} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value))} />
                      </td>
                      <td className="py-2 px-1">
                        <input type="number" step="0.01" className="w-24 border p-1 rounded text-xs" value={item.unit_value || 0} onChange={(e) => updateItem(idx, 'unit_value', parseFloat(e.target.value))} />
                      </td>
                      <td className="py-2 px-1">
                        <input type="number" step="0.01" className="w-20 border p-1 rounded text-xs" value={item.weight_kg || 0.3} onChange={(e) => updateItem(idx, 'weight_kg', parseFloat(e.target.value))} />
                      </td>
                      <td className="py-2 px-1">
                        <input className="w-20 border p-1 rounded text-xs" value={item.country_of_origin || 'España'} onChange={(e) => updateItem(idx, 'country_of_origin', e.target.value)} />
                      </td>
                      <td className="py-2 px-1 font-medium text-xs">
                        {((item.unit_value || 0) * (item.quantity || 1)).toFixed(2)}
                      </td>
                      <td className="py-2 px-1">
                        <button onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 text-xs">X</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals and notes */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs text-gray-500 font-medium">Observaciones / Notes</label>
              <textarea
                className="w-full border p-2 rounded text-sm"
                rows="3"
                value={data.notes || ''}
                onChange={(e) => setData({ ...data, notes: e.target.value })}
                placeholder="Observaciones para aduana..."
              />
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Valor total declarado (EUR)</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border p-2 rounded text-sm font-bold"
                  value={data.total_value || 0}
                  onChange={(e) => setData({ ...data, total_value: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Divisa / Currency</label>
                <select className="w-full border p-2 rounded text-sm" value={data.currency || 'EUR'} onChange={(e) => setData({ ...data, currency: e.target.value })}>
                  <option value="EUR">EUR - Euro</option>
                  <option value="USD">USD - Dolar USA</option>
                  <option value="GBP">GBP - Libra esterlina</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function RefundSection({ order, onRefundComplete }) {
  const [showRefund, setShowRefund] = useState(false);
  const [refundType, setRefundType] = useState('full');
  const [customAmount, setCustomAmount] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [refundNote, setRefundNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [totalRefunded, setTotalRefunded] = useState(0);

  const fetchRefunds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/refunds`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setRefunds(data.refunds || []);
      setTotalRefunded(data.total_refunded || 0);
    } catch (error) {
      console.error('Error fetching refunds:', error);
    }
  };

  useEffect(() => {
    if (showRefund) fetchRefunds();
  }, [showRefund, order.id]);

  const getRefundAmount = () => {
    if (refundType === 'full') return order.total;
    if (refundType === 'shipping') return order.shipping_cost || 0;
    if (refundType === 'product' && order.items?.[selectedProduct]) {
      const item = order.items[selectedProduct];
      return item.price * item.quantity;
    }
    if (refundType === 'custom') return parseFloat(customAmount) || 0;
    return 0;
  };

  const handleRefund = async () => {
    const amount = getRefundAmount();
    if (amount <= 0) { toast.error('Importe no válido'); return; }
    if (!confirm(`¿Confirmar reembolso de ${amount.toFixed(2)}€?`)) return;

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const body = { refund_type: refundType, note: refundNote };
      if (refundType === 'product') body.product_index = selectedProduct;
      if (refundType === 'custom') body.amount = parseFloat(customAmount);

      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/refund`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Reembolso de ${data.amount.toFixed(2)}€ procesado`);
        fetchRefunds();
        onRefundComplete();
        setRefundNote('');
      } else {
        toast.error(data.detail || 'Error al procesar reembolso');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setProcessing(false);
    }
  };

  const remaining = order.total - totalRefunded;

  return (
    <div className="border-t pt-4" data-testid="refund-section">
      <Button
        onClick={() => setShowRefund(!showRefund)}
        className="w-full border-2 border-orange-500 bg-white text-orange-600 hover:bg-orange-500 hover:text-white"
        data-testid="toggle-refund-btn"
      >
        Gestionar Reembolsos
      </Button>

      {showRefund && (
        <div className="mt-3 p-4 bg-orange-50 border-2 border-orange-200 rounded space-y-4">
          {/* Refund history */}
          {refunds.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-bold">Reembolsos realizados:</p>
              {refunds.map((r) => (
                <div key={r.id} className="bg-white p-2 rounded text-sm flex justify-between items-center border">
                  <div>
                    <p className="font-medium">{r.reason}</p>
                    {r.note && <p className="text-xs text-gray-500">{r.note}</p>}
                    <p className="text-xs text-gray-400">{new Date(r.created_at).toLocaleString('es-ES')}</p>
                  </div>
                  <span className="font-bold text-red-600">-{r.amount_eur?.toFixed(2) || (r.amount / 100).toFixed(2)}€</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-bold pt-2 border-t">
                <span>Total reembolsado:</span>
                <span className="text-red-600">{totalRefunded.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Disponible:</span>
                <span>{remaining.toFixed(2)}€</span>
              </div>
            </div>
          )}

          {remaining > 0 && (
            <>
              <div>
                <Label className="text-sm font-bold mb-2 block">Tipo de reembolso</Label>
                <select
                  className="w-full border-2 border-gray-300 p-2 rounded text-sm"
                  value={refundType}
                  onChange={(e) => setRefundType(e.target.value)}
                  data-testid="refund-type-select"
                >
                  <option value="full">Total ({order.total.toFixed(2)}€)</option>
                  {order.items?.map((item, idx) => (
                    <option key={idx} value="product">
                      Producto: {item.product_name} ({(item.price * item.quantity).toFixed(2)}€)
                    </option>
                  ))}
                  <option value="shipping">Gastos de envío ({(order.shipping_cost || 0).toFixed(2)}€)</option>
                  <option value="custom">Importe personalizado</option>
                </select>
              </div>

              {refundType === 'product' && order.items?.length > 1 && (
                <div>
                  <Label className="text-sm mb-1 block">Seleccionar producto</Label>
                  <select
                    className="w-full border-2 border-gray-300 p-2 rounded text-sm"
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(parseInt(e.target.value))}
                  >
                    {order.items.map((item, idx) => (
                      <option key={idx} value={idx}>{item.product_name} x{item.quantity} - {(item.price * item.quantity).toFixed(2)}€</option>
                    ))}
                  </select>
                </div>
              )}

              {refundType === 'custom' && (
                <div>
                  <Label className="text-sm mb-1 block">Importe (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={remaining}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder={`Máx: ${remaining.toFixed(2)}€`}
                    data-testid="refund-custom-amount"
                  />
                </div>
              )}

              <div>
                <Label className="text-sm mb-1 block">Nota (opcional)</Label>
                <Input
                  value={refundNote}
                  onChange={(e) => setRefundNote(e.target.value)}
                  placeholder="Motivo del reembolso..."
                  data-testid="refund-note"
                />
              </div>

              <div className="bg-white p-3 rounded border text-sm">
                <div className="flex justify-between font-bold">
                  <span>Importe a reembolsar:</span>
                  <span className="text-red-600">{getRefundAmount().toFixed(2)}€</span>
                </div>
              </div>

              <Button
                onClick={handleRefund}
                disabled={processing || getRefundAmount() <= 0}
                className="w-full bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-refund-btn"
              >
                {processing ? 'Procesando...' : `Confirmar Reembolso de ${getRefundAmount().toFixed(2)}€`}
              </Button>
            </>
          )}

          {remaining <= 0 && refunds.length > 0 && (
            <p className="text-sm text-center text-green-700 font-medium">Pedido completamente reembolsado</p>
          )}
        </div>
      )}
    </div>
  );
}

function OrderItemsEditor({ order, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState(order.items || []);
  const [manualDiscount, setManualDiscount] = useState(order.manual_discount || 0);
  const [discountType, setDiscountType] = useState(order.manual_discount_type || 'fixed');
  const [discountNote, setDiscountNote] = useState(order.manual_discount_note || '');
  const [saving, setSaving] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    setItems(order.items || []);
    setManualDiscount(order.manual_discount || 0);
    setDiscountType(order.manual_discount_type || 'fixed');
    setDiscountNote(order.manual_discount_note || '');
  }, [order]);

  const fetchProducts = async () => {
    if (allProducts.length > 0) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`);
      setAllProducts(await response.json());
    } catch (error) { console.error('Error:', error); }
  };

  const addProduct = (product) => {
    setItems(prev => [...prev, { product_id: product.id, product_name: product.name, quantity: 1, price: product.price }]);
    setSearchTerm('');
    setShowSearch(false);
  };

  const filteredProducts = searchTerm.length > 0
    ? allProducts.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || (p.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) || (p.sku || '').toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 8)
    : [];

  const updateItemField = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/items`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items,
          manual_discount: parseFloat(manualDiscount) || 0,
          manual_discount_type: discountType,
          manual_discount_note: discountNote
        })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Pedido actualizado');
        onUpdate(data.order);
        setEditing(false);
      } else {
        toast.error(data.detail || 'Error al actualizar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-gray-600">Productos</p>
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-items-btn" className="text-xs">Editar</Button>
        </div>
        {items.map((item, idx) => (
          <div key={`item-view-${item.product_id || idx}-${idx}`} className="text-sm border-b pb-2 mb-2">
            <p className="font-medium">{item.product_name}</p>
            {item.variant && Object.entries(item.variant).filter(([k, v]) => v).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {Object.entries(item.variant).filter(([k, v]) => v).map(([k, v]) => (
                  <span key={k} className="bg-gray-100 text-xs px-2 py-0.5 rounded">{k}: {v}</span>
                ))}
              </div>
            )}
            <p className="text-gray-600">Cantidad: {item.quantity} x {item.price?.toFixed(2)}€ = {(item.price * item.quantity).toFixed(2)}€</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="border-2 border-orange-300 bg-orange-50 p-3 rounded" data-testid="order-items-editor">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold">Editar Productos</p>
        <Button variant="outline" size="sm" onClick={() => { setEditing(false); setItems(order.items || []); setManualDiscount(order.manual_discount || 0); setDiscountType(order.manual_discount_type || 'fixed'); }} className="text-xs">Cancelar</Button>
      </div>

      {items.map((item, idx) => (
        <div key={`item-edit-${item.product_id || idx}-${idx}`} className="grid grid-cols-12 gap-1 mb-2 items-center bg-white p-2 rounded border text-sm">
          <div className="col-span-5">
            <p className="font-medium text-xs truncate">{item.product_name}</p>
          </div>
          <div className="col-span-2">
            <input type="number" min="1" className="w-full border p-1 rounded text-sm text-center" value={item.quantity} onChange={(e) => updateItemField(idx, 'quantity', parseInt(e.target.value) || 1)} />
          </div>
          <div className="col-span-3">
            <input type="number" step="0.01" min="0" className="w-full border p-1 rounded text-sm text-right" value={item.price} onChange={(e) => updateItemField(idx, 'price', parseFloat(e.target.value) || 0)} />
          </div>
          <div className="col-span-1 text-right">
            <span className="font-bold text-xs">{(item.price * item.quantity).toFixed(2)}€</span>
          </div>
          <div className="col-span-1">
            <button onClick={() => removeItem(idx)} className="text-red-600 text-xs font-bold hover:text-red-800">X</button>
          </div>
        </div>
      ))}

      {/* Add product */}
      {showSearch ? (
        <div className="relative mb-3">
          <input
            type="text"
            className="w-full border-2 border-black p-2 rounded text-sm"
            placeholder="Buscar producto por nombre, marca o SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          <button onClick={() => { setShowSearch(false); setSearchTerm(''); }} className="absolute right-2 top-2 text-gray-400 hover:text-black text-sm">X</button>
          {filteredProducts.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border-2 border-black rounded-b shadow-lg z-10 max-h-48 overflow-y-auto">
              {filteredProducts.map(p => (
                <button key={p.id} type="button" onClick={() => addProduct(p)} className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b text-sm flex justify-between">
                  <span className="font-medium">{p.name}</span>
                  <span className="text-gray-600 ml-2">{p.price.toFixed(2)}€</span>
                </button>
              ))}
            </div>
          )}
          {searchTerm.length > 0 && filteredProducts.length === 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border-2 border-gray-300 rounded-b p-2 text-sm text-gray-500">Sin resultados</div>
          )}
        </div>
      ) : (
        <button type="button" onClick={() => { setShowSearch(true); fetchProducts(); }} className="w-full border-2 border-dashed border-gray-400 py-2 rounded text-sm text-gray-600 hover:border-black hover:text-black transition-colors mb-3" data-testid="add-item-to-order-btn">
          + Añadir producto
        </button>
      )}

      <div className="mt-3 pt-3 border-t space-y-2">
        <div>
          <label className="text-xs font-bold block mb-1">Descuento manual</label>
          <div className="flex gap-2 items-center">
            <select
              className="border-2 border-orange-300 p-1.5 rounded text-sm"
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value)}
              data-testid="discount-type-select"
            >
              <option value="fixed">€ (fijo)</option>
              <option value="percentage">% (porcentaje)</option>
            </select>
            <input
              type="number" step="0.01" min="0" max={discountType === 'percentage' ? 100 : undefined}
              className="w-24 border-2 border-orange-300 p-1.5 rounded text-sm"
              value={manualDiscount}
              onChange={(e) => setManualDiscount(e.target.value)}
              data-testid="manual-discount-input"
            />
            <span className="text-xs text-gray-500">
              {discountType === 'percentage' && manualDiscount > 0
                ? `= ${(items.reduce((s, i) => s + i.price * i.quantity, 0) * (parseFloat(manualDiscount) || 0) / 100).toFixed(2)}€`
                : ''}
            </span>
            <input
              type="text"
              className="flex-1 border p-1.5 rounded text-sm"
              placeholder="Motivo del descuento..."
              value={discountNote}
              onChange={(e) => setDiscountNote(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <Button onClick={handleSave} disabled={saving} className="bg-black text-white text-sm" data-testid="save-items-btn">
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>
    </div>
  );
}

function PaymentMethodEditor({ order, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(order.payment_method || 'efectivo');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(order.payment_method || 'efectivo'); }, [order.id, order.payment_method]);

  const badgeColor = {
    bizum: 'text-red-600',
    transferencia: 'text-orange-600',
    stripe: 'text-purple-600',
    datafono_caixabank: 'text-blue-700',
    efectivo: 'text-gray-800',
  }[order.payment_method] || 'text-gray-800';

  const save = async () => {
    if (value === order.payment_method) { setEditing(false); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/payment-method`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ payment_method: value }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || 'Error');
      toast.success('Método de pago actualizado');
      if (body.order) onUpdate(body.order); else onUpdate({ payment_method: value });
      setEditing(false);
    } catch (e) {
      toast.error(e.message || 'No se pudo actualizar');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2 mb-1" data-testid="payment-method-display">
        <p className={`font-bold uppercase ${badgeColor}`}>{order.payment_method}</p>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-blue-600 hover:underline"
          data-testid="payment-method-edit-btn"
        >
          Cambiar
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 mb-1" data-testid="payment-method-editor">
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="border border-gray-300 rounded px-2 py-1 text-sm"
        disabled={saving}
        data-testid="payment-method-select"
      >
        <option value="efectivo">Efectivo</option>
        <option value="transferencia">Transferencia</option>
        <option value="bizum">Bizum</option>
        <option value="stripe">Stripe</option>
        <option value="datafono_caixabank">Datáfono virtual CaixaBank</option>
      </select>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="text-xs bg-black text-white px-3 py-1 rounded hover:bg-gray-800 disabled:bg-gray-400"
        data-testid="payment-method-save-btn"
      >
        {saving ? '…' : 'Guardar'}
      </button>
      <button
        type="button"
        onClick={() => { setValue(order.payment_method || 'efectivo'); setEditing(false); }}
        disabled={saving}
        className="text-xs text-gray-600 hover:text-black"
        data-testid="payment-method-cancel-btn"
      >
        Cancelar
      </button>
    </div>
  );
}


function PaymentStatusControls({ order, onUpdate }) {
  const [syncing, setSyncing] = useState(false);

  const syncStripe = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/sync-stripe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.new_status === 'paid') {
        toast.success('Pago confirmado en Stripe');
        onUpdate({ ...order, payment_status: 'paid' });
      } else {
        toast.info(data.message || data.error || 'Sin cambios');
      }
    } catch (error) { toast.error('Error de conexión'); }
    finally { setSyncing(false); }
  };

  const changeStatus = async (newStatus) => {
    if (!confirm(`¿Cambiar estado de pago a "${newStatus}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/payment-status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_status: newStatus })
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        toast.success(`Estado cambiado a ${newStatus}`);
        // Notify admin about the confirmation email outcome (bizum/transferencia)
        if (data.email_sent === true) {
          toast.success('Email "Pago recibido" enviado al cliente');
        } else if (data.email_sent === false) {
          toast.error(`Email al cliente NO enviado: ${data.email_error || 'error desconocido'}`, { duration: 8000 });
        }
        onUpdate({ ...order, payment_status: newStatus });
      }
    } catch (error) { toast.error('Error'); }
  };

  return (
    <div className="mt-2 space-y-2" data-testid="payment-controls">
      {order.payment_method === 'stripe' && order.payment_status === 'pending' && (
        <Button onClick={syncStripe} disabled={syncing} className="w-full bg-blue-600 text-white hover:bg-blue-700 text-sm" data-testid="sync-stripe-btn">
          {syncing ? 'Consultando Stripe...' : 'Sincronizar con Stripe'}
        </Button>
      )}
      {order.payment_status === 'pending' && (
        <Button onClick={() => changeStatus('paid')} className="w-full bg-green-600 text-white hover:bg-green-700 text-sm" data-testid="mark-paid-btn">
          Marcar como Pagado
        </Button>
      )}
      {order.payment_status === 'paid' && (
        <Button onClick={() => changeStatus('pending')} variant="outline" className="w-full text-sm border-yellow-500 text-yellow-700">
          Volver a Pendiente
        </Button>
      )}
    </div>
  );
}


function PaymentEmailStatus({ order, onUpdate }) {
  const [resending, setResending] = useState(false);
  const method = (order.payment_method || '').toLowerCase();
  const isRelevant = order.payment_status === 'paid' && (method === 'bizum' || method === 'transferencia');
  if (!isRelevant) return null;

  const sentAt = order.payment_confirmed_email_sent_at;
  const err = order.payment_confirmed_email_error;

  const resend = async () => {
    if (!window.confirm('¿Reenviar email "Pago recibido" al cliente?')) return;
    setResending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/resend-payment-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (data.email_sent) {
        toast.success('Email reenviado');
        onUpdate({ payment_confirmed_email_sent_at: new Date().toISOString(), payment_confirmed_email_error: null });
      } else {
        toast.error(`No se pudo enviar: ${data.email_error || 'error desconocido'}`, { duration: 8000 });
        onUpdate({ payment_confirmed_email_error: data.email_error });
      }
    } catch (e) {
      toast.error('Error de conexión');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="mt-2 text-xs" data-testid="payment-email-status">
      {sentAt && !err && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-2 py-1">
          <span className="text-green-700">✓ Email "Pago recibido" enviado el {new Date(sentAt).toLocaleString('es-ES')}</span>
          <button onClick={resend} disabled={resending} className="text-green-700 underline text-xs">
            {resending ? '...' : 'Reenviar'}
          </button>
        </div>
      )}
      {!sentAt && err && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded px-2 py-1">
          <span className="text-red-700">✗ Email no enviado: {err.slice(0, 80)}</span>
          <button onClick={resend} disabled={resending} className="bg-red-600 text-white px-2 py-1 rounded text-xs">
            {resending ? '...' : 'Reintentar'}
          </button>
        </div>
      )}
      {!sentAt && !err && (
        <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
          <span className="text-yellow-800">⚠ Email pendiente de envío</span>
          <button onClick={resend} disabled={resending} className="bg-yellow-600 text-white px-2 py-1 rounded text-xs">
            {resending ? '...' : 'Enviar ahora'}
          </button>
        </div>
      )}
    </div>
  );
}


function ContactClientSection({ order }) {
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState(`Sobre tu pedido #${(order.id || '').slice(0, 8)}`);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setHistory(data.messages || []);
    } catch (error) { console.error('Error:', error); }
  };

  useEffect(() => {
    if (showForm) fetchHistory();
  }, [showForm, order.id]);

  const sendMessage = async () => {
    if (!message.trim()) { toast.error('Escribe un mensaje'); return; }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/message`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, message })
      });
      if (response.ok) {
        toast.success('Mensaje enviado al cliente');
        setMessage('');
        fetchHistory();
      } else {
        const d = await response.json().catch(() => ({}));
        toast.error(d.detail || 'Error al enviar');
      }
    } catch (error) { toast.error('Error de conexión'); }
    finally { setSending(false); }
  };

  return (
    <div className="border-t pt-4" data-testid="contact-client-section">
      <Button onClick={() => setShowForm(!showForm)} className="w-full border-2 border-blue-500 bg-white text-blue-600 hover:bg-blue-500 hover:text-white text-sm" data-testid="toggle-contact-btn">
        Contactar con el Cliente
      </Button>

      {showForm && (
        <div className="mt-3 p-4 bg-blue-50 border-2 border-blue-200 rounded space-y-3">
          <p className="text-xs text-gray-600">Se enviará email a: <strong>{order.user_email}</strong></p>

          {history.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              <p className="text-xs font-bold text-gray-600">Mensajes anteriores:</p>
              {history.map((msg) => (
                <div key={msg.id} className="bg-white p-2 rounded text-xs border">
                  <p className="font-medium">{msg.subject}</p>
                  <p className="text-gray-600 mt-1">{msg.message?.slice(0, 100)}...</p>
                  <p className="text-gray-400 mt-1">{new Date(msg.sent_at).toLocaleString('es-ES')}</p>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="text-xs font-bold block mb-1">Asunto</label>
            <input className="w-full border p-2 rounded text-sm" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-bold block mb-1">Mensaje</label>
            <textarea className="w-full border p-2 rounded text-sm" rows="4" placeholder="Escribe tu mensaje al cliente..." value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <Button onClick={sendMessage} disabled={sending} className="w-full bg-blue-600 text-white hover:bg-blue-700 text-sm" data-testid="send-message-btn">
            {sending ? 'Enviando...' : 'Enviar Email al Cliente'}
          </Button>
        </div>
      )}
    </div>
  );
}


// Small component rendering a chronological log of emails sent for a given
// order. Renders nothing if the order has no notifications yet.
const NOTIFICATION_LABELS = {
  order_processing: { label: 'Procesando', icon: '⚙️' },
  order_shipped: { label: 'Enviado / Tracking', icon: '🚚' },
  manual_processing: { label: 'Procesando (manual)', icon: '⚙️' },
  manual_shipped: { label: 'Enviado (manual)', icon: '🚚' },
  credentials_new: { label: 'Cuenta creada', icon: '🆕' },
  credentials_resend: { label: 'Credenciales reenviadas', icon: '🔑' },
  guest_activation: { label: 'Activación 1-click', icon: '✨' },
  bizum_payment: { label: 'Pago Bizum', icon: '💳' },
  transfer_payment: { label: 'Pago Transferencia', icon: '🏦' },
};

function formatNotificationDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return iso;
  }
}

function NotificationsLog({ log }) {
  // Defensive sort by sent_at desc — tolerates out-of-order writes in the DB.
  const entries = Array.isArray(log)
    ? [...log].sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''))
    : [];
  return (
    <div className="mt-3 p-3 bg-white border rounded" data-testid="notifications-log-section">
      <p className="text-sm font-medium mb-2">📨 Historial de emails enviados</p>
      {entries.length === 0 ? (
        <p className="text-xs text-gray-500" data-testid="notifications-log-empty">
          Sin emails registrados todavía para este pedido.
        </p>
      ) : (
        <ul className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="notifications-log-list">
          {entries.map((e, idx) => {
            const meta = NOTIFICATION_LABELS[e.type] || { label: e.type, icon: '📧' };
            return (
              <li key={`${e.sent_at}-${idx}`} className="flex items-start gap-2 text-xs text-gray-700">
                <span className="flex-shrink-0">{meta.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="leading-tight">
                    <strong>{meta.label}</strong>
                    <span className="text-gray-500"> · {formatNotificationDate(e.sent_at)}</span>
                  </p>
                  <p className="text-gray-500 truncate">
                    a {e.recipient}
                    {e.sent_by ? ` · por ${e.sent_by}` : ''}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}



const PARTIAL_METHOD_LABELS = {
  tarjeta: 'Tarjeta',
  efectivo: 'Efectivo',
  bizum: 'Bizum',
  transferencia: 'Transferencia',
  stripe: 'Stripe',
  datafono_caixabank: 'Datáfono CaixaBank',
  otro: 'Otro',
};

function PartialPaymentsSection({ order, onUpdate }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('tarjeta');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const total = Number(order?.total || 0);
  const paid = Number(order?.paid_amount || 0);
  const outstanding = Math.max(0, +(total - paid).toFixed(2));
  const payments = Array.isArray(order?.payments) ? order.payments : [];

  const addPayment = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) {
      toast.error('Indica un importe mayor que 0');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/payments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: value, method, note: note.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const overpayment = Number(data.overpayment || 0);
        toast.success(`Pago registrado: ${value.toFixed(2)} € (${PARTIAL_METHOD_LABELS[method] || method})`);
        if (overpayment > 0) {
          toast.warning(`Atención: se ha pagado ${overpayment.toFixed(2)} € de más sobre el total del pedido.`);
        }
        // Notify admin about the confirmation email outcome (bizum/transferencia)
        if (data.email_sent === true) {
          toast.success('Email "Pago recibido" enviado al cliente');
        } else if (data.email_sent === false) {
          toast.error(`Email al cliente NO enviado: ${data.email_error || 'error desconocido'}`, { duration: 8000 });
        }
        onUpdate({
          payments: [...payments, data.payment],
          paid_amount: data.paid_amount,
          payment_status: data.payment_status,
        });
        setAmount('');
        setNote('');
      } else {
        toast.error(data.detail || 'No se pudo registrar el pago');
      }
    } catch (e) {
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  const removePayment = async (paymentId) => {
    if (!window.confirm('¿Eliminar este pago del registro?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Pago eliminado');
        onUpdate({
          payments: payments.filter(p => p.id !== paymentId),
          paid_amount: data.paid_amount,
          payment_status: data.payment_status,
        });
      } else {
        toast.error(data.detail || 'No se pudo eliminar');
      }
    } catch (e) {
      toast.error('Error de conexión');
    }
  };

  const fillOutstanding = () => setAmount(outstanding > 0 ? outstanding.toFixed(2) : '');
  const fillHalf = () => {
    if (total > 0) setAmount((total / 2).toFixed(2));
  };

  return (
    <div className="border-t pt-4" data-testid="partial-payments-section">
      <p className="font-medium mb-2">Pagos parciales</p>

      <div className="grid grid-cols-3 gap-2 text-sm mb-3 p-3 bg-gray-50 border rounded">
        <div>
          <p className="text-gray-500 text-xs">Total pedido</p>
          <p className="font-bold">{total.toFixed(2)} €</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">Pagado</p>
          <p className="font-bold text-green-700" data-testid="partial-paid-amount">{paid.toFixed(2)} €</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">Pendiente</p>
          <p className={`font-bold ${outstanding > 0 ? 'text-red-700' : 'text-green-700'}`} data-testid="partial-outstanding-amount">
            {outstanding.toFixed(2)} €
          </p>
        </div>
      </div>

      {payments.length > 0 && (
        <ul className="space-y-1 mb-3 max-h-44 overflow-y-auto" data-testid="partial-payments-list">
          {payments.map((p) => (
            <li key={p.id} className="flex items-start justify-between gap-2 text-xs bg-white border p-2 rounded">
              <div className="flex-1 min-w-0">
                <p className="font-medium">
                  {Number(p.amount).toFixed(2)} € · {PARTIAL_METHOD_LABELS[p.method] || p.method}
                </p>
                <p className="text-gray-500">
                  {p.paid_at ? new Date(p.paid_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  {p.registered_by ? ` · ${p.registered_by}` : ''}
                </p>
                {p.note && <p className="text-gray-600 italic truncate">{p.note}</p>}
              </div>
              <button
                type="button"
                onClick={() => removePayment(p.id)}
                className="text-red-600 hover:text-red-800 text-xs"
                title="Eliminar pago"
                data-testid={`remove-partial-payment-${p.id}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2 p-3 bg-white border-2 border-black rounded">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Importe (€)</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="text-sm"
              data-testid="partial-amount-input"
            />
          </div>
          <div>
            <Label className="text-xs">Método</Label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full text-sm border-2 border-black p-2 rounded"
              data-testid="partial-method-select"
            >
              {Object.entries(PARTIAL_METHOD_LABELS).map(([val, lbl]) => (
                <option key={val} value={val}>{lbl}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Nota (opcional)</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ej: 50% adelantado, resto en entrega"
            className="text-sm"
            data-testid="partial-note-input"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fillHalf}
            className="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100"
            data-testid="fill-half-btn"
          >
            50%
          </button>
          <button
            type="button"
            onClick={fillOutstanding}
            disabled={outstanding <= 0}
            className="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100 disabled:opacity-50"
            data-testid="fill-outstanding-btn"
          >
            Pendiente ({outstanding.toFixed(2)} €)
          </button>
        </div>
        <Button
          onClick={addPayment}
          disabled={submitting}
          className="w-full bg-black text-white hover:bg-gray-800"
          data-testid="add-partial-payment-btn"
        >
          {submitting ? 'Registrando...' : '+ Registrar pago'}
        </Button>
        <p className="text-xs text-gray-500">
          💡 Útil para pagos mixtos (tarjeta + efectivo) o personalizados al 50%. Al alcanzar el total el pedido pasa automáticamente a "pagado".
        </p>
      </div>
    </div>
  );
}
